import { Checkbox, DefaultButton, IconButton, IContextualMenuItem, Label, MessageBar, MessageBarType, Panel, PanelType, Spinner, Stack, StackItem } from '@fluentui/react';
import React from 'react';
import { toast } from 'react-toastify';
import { useAddProcurementDemandItemToOrderMutation, useCreateProcurementOrderMutation, useGetProcurementDemandItemQuery } from '../../../store/endpoints/ProcurementEndpoints';
import { humanizeRecordType, IRecordLink, useGetDemandItemLinksQuery, useLazyResolveRecordUrlQuery } from '../../../store/endpoints/RecordLinksEndpoints';
import OptionSelectionToggle from '../../Common/OptionSelectionToggle';
import AccordionBox from '../../Common/AccordionBox';
import { useGetItemDetailsQuery } from '../../../store/Api';
import { ICreateProcurementOrderRequest, IProcurementCompany, IProcurementDemandItem } from '../../../models/Procurement';
import VendorDisplay from '../../Products/VendorDisplay';
import DataField from '../../Common/DataField';
import ToggleField from '../../Common/ToggleField';
import ExistingOrderSearch from '../ProcureProduct/ExistingOrderSearch';
import CompanyForm from '../ProcureProduct/CompanyForm';
import { isEmptyCompany, useDefaultShipmentContact } from '../useDefaultShipmentContact';

interface IProcureDemandPanelProps {
    demandId: string;
    onDismiss: () => void;
    onProcure: (orderId: string) => void;
}

const PROCUREMENT_METHOD_NEW_ORDER = 'Create Order';
const PROCUREMENT_METHOD_ADD_TO_EXISTING = 'Add to Order';

export const STEP_NONE = 'None';
export const STEP_REVIEW = 'Review';
export const STEP_RESOURCE = 'Re-Source';
export const STEP_NEW_ORDER = 'New Order';
export const STEP_SHIP_TO = 'Ship To';
export const STEP_BILL_TO = 'Bill To';
export const STEP_EXISTING_ORDERS = 'Existing Orders';

const ProcureDemandPanel: React.FC<IProcureDemandPanelProps> = (props) => {
    const demandData = useGetProcurementDemandItemQuery({ itemId: props.demandId });
    const [createOrder] = useCreateProcurementOrderMutation();
    const [addToOrder] = useAddProcurementDemandItemToOrderMutation();
    const [method, setMethod] = React.useState<string>(PROCUREMENT_METHOD_NEW_ORDER);
    const [currentStep, setCurrentStep] = React.useState<string>(STEP_REVIEW);
    const [demand, setDemand] = React.useState<IProcurementDemandItem | undefined>(undefined);
    const [createOrderDetails, setCreateOrderDetails] = React.useState<Partial<ICreateProcurementOrderRequest>>({});
    const [existingOrderId, setExistingOrderId] = React.useState<string | undefined>(undefined);
    const [error, setError] = React.useState<string | undefined>(undefined);
    const [processingMessage, setProcessingMessage] = React.useState<string | undefined>(undefined);

    const links = useGetDemandItemLinksQuery(props.demandId, { skip: !props.demandId });
    const [resolveRecordUrl] = useLazyResolveRecordUrlQuery();

    React.useEffect(() => {
        if (demandData.data) {
            setDemand(demandData.data);
        } else {
            setDemand(undefined);
        }
    }, [demandData.data]);

    const openRecord = async (link: IRecordLink) => {
        try {
            const res = await resolveRecordUrl({ folioId: link.folioId, recordType: link.recordType, recordId: link.recordId }).unwrap();
            if (res.supported && res.url) {
                window.open(res.url, '_blank', 'noopener');
            } else {
                toast.error(`This record can't be opened in ${link.folioName ?? 'the CRM'}`);
            }
        } catch {
            toast.error('Failed to open record');
        }
    };

    const actionItems: IContextualMenuItem[] = (links.data ?? []).map(link => ({
        key: `${link.folioId}-${link.recordType}-${link.recordId}`,
        text: `Open in ${link.folioName ?? 'CRM'}`,
        iconProps: { iconName: 'OpenInNewWindow' },
        disabled: !link.supported,
        title: link.supported ? undefined : `${link.folioName ?? 'This folio'} does not support direct linking to ${humanizeRecordType(link.recordType)}`,
        onClick: link.supported ? () => { void openRecord(link); } : undefined
    }));

    const procure = async () => {
        if (!demand) return;
        setError(undefined);
        if (method === PROCUREMENT_METHOD_NEW_ORDER) {
            setProcessingMessage('Creating procurement order...');
            const result = await createOrder({
                order: {
                    vendor: demand.vendor!,
                    ...createOrderDetails,
                    items: [{
                        catalog: demand.catalog,
                        productId: demand.productId,
                        quantity: demand.quantity,
                        procurementDemandId: demand.id,
                    }]
                }
            });
            setProcessingMessage(undefined);
            if (!('data' in result) || !result.data.id) {
                setError('Error creating procurement order');
                return;
            }
            props.onProcure(result.data.id);
        } else if (method === PROCUREMENT_METHOD_ADD_TO_EXISTING) {
            if (!existingOrderId) return;
            setProcessingMessage('Adding item to order...');
            const result = await addToOrder({
                itemId: demand.id,
                orderId: existingOrderId,
                data: {}
            });
            setProcessingMessage(undefined);
            if (!('data' in result) || !result.data.success) {
                setError('Error adding item to order');
                return;
            }
            props.onProcure(existingOrderId);
        }
    }

    const validate = () => {
        if (!demand) return false;
        if (method === PROCUREMENT_METHOD_NEW_ORDER) {
            return !!demand.vendor;
        }
        if (method === PROCUREMENT_METHOD_ADD_TO_EXISTING) {
            return !!existingOrderId;
        }
        return false;
    }

    return (
        <Panel
            type={PanelType.medium}
            isOpen={true}
            onDismiss={props.onDismiss}
            isFooterAtBottom={true}
            headerText={'Procure Demand Item'}
            onRenderHeader={() => <div className='ms-Panel-header' style={{ paddingLeft: '24px', paddingRight: '0.5em', alignSelf: 'flex-start', flexGrow: 1 }}>
                <div className='ms-Panel-headerText'>
                    <Stack horizontal verticalAlign='center'>
                        <StackItem>
                            <span style={{ fontSize: '20px', fontWeight: 600, color: 'rgb(50, 49, 48)', lineHeight: '27px' }}>Procure Demand Item</span>
                        </StackItem>
                        {actionItems.length > 0 && <StackItem styles={{ root: { marginLeft: 'auto' } }}>
                            <IconButton
                                iconProps={{ iconName: 'MoreVertical' }}
                                title='More actions'
                                ariaLabel='More actions'
                                onRenderMenuIcon={() => null}
                                menuProps={{ items: actionItems }}
                            />
                        </StackItem>}
                    </Stack>
                </div>
            </div>}
            onRenderFooter={() => <Stack horizontal tokens={{ childrenGap: '1em' }} style={{ padding: '1em' }}>
                <DefaultButton onClick={props.onDismiss} style={{ flexGrow: 1 }} disabled={processingMessage !== undefined}>Cancel</DefaultButton>
                <DefaultButton primary onClick={procure} style={{ flexGrow: 1 }} disabled={!validate() || processingMessage !== undefined}>{processingMessage ?? 'Procure'}</DefaultButton>
            </Stack>}
        >
            {error && <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>}
            {demand == null ? <Spinner label='Loading demand item...' /> : (
                <Stack style={{ paddingTop: '1em' }}>
                    <OptionSelectionToggle
                        options={[
                            { key: PROCUREMENT_METHOD_NEW_ORDER, text: PROCUREMENT_METHOD_NEW_ORDER },
                            { key: PROCUREMENT_METHOD_ADD_TO_EXISTING, text: PROCUREMENT_METHOD_ADD_TO_EXISTING }
                        ]}
                        selectedKey={method}
                        onChange={setMethod}
                    />
                    <AccordionBox
                        title={STEP_REVIEW}
                        isExpanded={currentStep === STEP_REVIEW}
                        onToggle={(expanded, itemKey) => {
                            if (itemKey === undefined) return;
                            if (!expanded) setCurrentStep(STEP_NONE);
                            else setCurrentStep(itemKey);
                        }}
                        itemKey={STEP_REVIEW}
                    >
                        <table className='data-table'>
                            <tbody>
                                <tr>
                                    <td>Product</td>
                                    <td>{demand?.productName}</td>
                                </tr>
                                <tr>
                                    <td>Manufacturer</td>
                                    <td>{demand?.manufacturer}</td>
                                </tr>
                                <tr>
                                    <td>Quantity</td>
                                    <td>{demand?.quantity}</td>
                                </tr>
                                <tr>
                                    <td>Last Update</td>
                                    <td>{demand?.lastUpdated ? new Date(demand.lastUpdated).toLocaleString() : ''}</td>
                                </tr>
                                <tr>
                                    <td>Source</td>
                                    <td>{demand?.storefrontOrderId ? 'Storefront Order' : 'Manual'}</td>
                                </tr>
                                <tr>
                                    <td>Customer</td>
                                    <td>{demand?.customerEmail}</td>
                                </tr>
                            </tbody>
                        </table>
                    </AccordionBox>
                    <ReSourceStep
                        catalog={demand.catalog}
                        productId={demand.productId}
                        currentVendor={demand.vendor || ''}
                        onChange={(vendor) => {
                            setDemand({
                                ...demand,
                                vendor: vendor
                            });
                        }}
                        isExpanded={currentStep === STEP_RESOURCE}
                        onToggle={(expanded, itemKey) => {
                            if (itemKey === undefined) return;
                            if (!expanded) setCurrentStep(STEP_NONE);
                            else setCurrentStep(itemKey);
                        }}
                    />
                    {method === PROCUREMENT_METHOD_NEW_ORDER ? (
                        <>
                            <OrderDetailsStep
                                details={createOrderDetails}
                                onChange={setCreateOrderDetails}
                                step={currentStep}
                                setStep={setCurrentStep}
                            />
                            <ShipToStep
                                shipTo={createOrderDetails.shipTo}
                                onChange={(shipTo) => setCreateOrderDetails({ ...createOrderDetails, shipTo })}
                                dropship={createOrderDetails.dropship}
                                dropshipShipTo={demand.shipTo}
                                step={currentStep}
                                setStep={setCurrentStep}
                            />
                            <BillToStep
                                billTo={createOrderDetails.billTo}
                                onChange={(billTo) => setCreateOrderDetails({ ...createOrderDetails, billTo })}
                                step={currentStep}
                                setStep={setCurrentStep}
                            />
                        </>
                    ) : null}
                    {method === PROCUREMENT_METHOD_ADD_TO_EXISTING ? (
                        <FindExistingOrderStep
                            selectedOrderId={existingOrderId}
                            onOrderSelected={setExistingOrderId}
                            step={currentStep}
                            setStep={setCurrentStep}
                            vendor={demand.vendor}
                        />
                    ) : null}
                </Stack>
            )}
        </Panel>
    );
}

interface IReSourceStepProps {
    catalog: string;
    productId: string;
    currentVendor: string;
    onChange: (vendor: string) => void;
    isExpanded: boolean;
    onToggle: (expanded: boolean, itemKey?: string) => void;
}

const ReSourceStep: React.FC<IReSourceStepProps> = (props) => {
    const product = useGetItemDetailsQuery({ catalog: props.catalog, id: props.productId });
    const [currentCost, setCurrentCost] = React.useState<number | undefined>(undefined);

    React.useEffect(() => {
        if (product.data) {
            const vendor = product.data.vendors?.find(v => v.vendorName === props.currentVendor);
            setCurrentCost(vendor?.cost);
        } else {
            setCurrentCost(undefined);
        }
    }, [product.data, props.currentVendor]);

    if (product.isLoading) {
        return <Spinner label='Loading product details' />;
    }
    if (!product.data) {
        return <div>Could not load product details</div>;
    }


    return (
        <AccordionBox
            title={'Re-Source'}
            secondaryTitle={`${props.currentVendor} ${currentCost != null ? `- ${currentCost.toLocaleString(undefined, { style: 'currency', currency: 'USD' })}` : ''}`}
            isExpanded={props.isExpanded}
            onToggle={props.onToggle}
            itemKey={STEP_RESOURCE}
        >
            <table className='data-table'>
                <thead>
                    <tr>
                        <th></th>
                        <th>Vendor</th>
                        <th>Cost</th>
                    </tr>
                </thead>
                <tbody>
                    {product.data?.vendors?.map((vendor, index) => (
                        <tr key={index} onClick={() => props.onChange(vendor.vendorName)} style={{ cursor: 'pointer', backgroundColor: props.currentVendor === vendor.vendorName ? '#eaeaea' : 'transparent' }}>
                            <td>
                                <Checkbox checked={props.currentVendor === vendor.vendorName} onChange={() => props.onChange(vendor.vendorName)} />
                            </td>
                            <td>
                                <p style={{ fontWeight: 'bold', margin: 0 }}>
                                    {vendor.vendorName}
                                </p>
                                <p style={{ margin: 0 }}>{vendor.vendorPartNumber}</p>
                            </td>
                            <td><VendorDisplay vendor={[vendor]} product={product.data!} dense /></td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </AccordionBox>
    );
}

export const OrderDetailsStep: React.FC<{
    details: Partial<ICreateProcurementOrderRequest>;
    onChange: (details: Partial<ICreateProcurementOrderRequest>) => void;
    step: string;
    setStep: (step: string) => void;
    disabled?: boolean;
}> = (props) => {
    return (
        <AccordionBox
            title={`Order Details`}
            isExpanded={props.step === STEP_NEW_ORDER}
            onToggle={(expanded) => {
                if (expanded) {
                    props.setStep(STEP_NEW_ORDER);
                } else {
                    props.setStep(STEP_NONE);
                }
            }}
            disabled={props.disabled}
        >
            <DataField
                label='Order Name'
                value={props.details.name}
                onChange={(ev, newValue) => props.onChange({ ...props.details, name: newValue })}
                />
            <DataField
                label='Purchase Order Number'
                value={props.details.purchaseOrderNumber}
                onChange={(ev, newValue) => props.onChange({ ...props.details, purchaseOrderNumber: newValue })}
            />
            <DataField
                label='Internal Notes'
                value={props.details.internalOrderNotes}
                onChange={(ev, newValue) => props.onChange({ ...props.details, internalOrderNotes: newValue })}
                multiline
                toolTip='Order notes internal to your company and not shared with the vendor or customer'
            />
            <DataField
                label='Vendor Notes'
                value={props.details.vendorNotes}
                onChange={(ev, newValue) => props.onChange({ ...props.details, vendorNotes: newValue })}
                multiline
                toolTip='Notes for the vendor regarding this order. Sent to supported vendors via extension when placing order.'
            />
            <ToggleField
                label='Allow Backorder'
                checked={props.details.allowBackorder || false}
                onChange={(ev, checked) => props.onChange({ ...props.details, allowBackorder: checked })}
                toolTip='For supported vendors, if selected, items that are out of stock will be backordered instead of cancelling them.'
            />
            <ToggleField
                label='Dropship'
                checked={props.details.dropship || false}
                onChange={(ev, checked) => props.onChange({ ...props.details, dropship: checked })}
                toolTip='If enabled, the order ships directly to the end customer — the ship-to is suggested from the demand. Otherwise it ships to your default shipment location.'
            />
        </AccordionBox>
    );
}

/**
 * Ship-to for a new order, with an auto-suggested address: the carried demand ship-to when the order
 * is a dropship, otherwise the organization's default shipment contact. Re-suggests when Dropship is
 * toggled; the user can still edit or replace the address.
 */
export const ShipToStep: React.FC<{
    shipTo?: IProcurementCompany;
    onChange: (shipTo?: IProcurementCompany) => void;
    dropship?: boolean;
    dropshipShipTo?: IProcurementCompany;
    step: string;
    setStep: (step: string) => void;
    disabled?: boolean;
}> = (props) => {
    const defaultContact = useDefaultShipmentContact();
    // Remember the previous Dropship value so we only force a re-suggest on an explicit toggle,
    // and otherwise just fill an empty ship-to as the suggestion sources load.
    const prevDropship = React.useRef<boolean | undefined>(undefined);

    const dropshipShipTo = props.dropshipShipTo;
    const { dropship, shipTo, onChange } = props;
    React.useEffect(() => {
        const dropshipToggled = prevDropship.current !== dropship;
        prevDropship.current = dropship;
        const suggestion = dropship ? dropshipShipTo : defaultContact;
        if (suggestion && (dropshipToggled || isEmptyCompany(shipTo))) {
            onChange(suggestion);
        }
    }, [dropship, dropshipShipTo, defaultContact, shipTo, onChange]);

    const hint = props.dropship
        ? 'Dropship order — suggesting the ship-to carried from the demand.'
        : 'Suggesting your default shipment contact. Enable Dropship to ship to the end customer instead.';

    return (
        <AccordionBox
            title={STEP_SHIP_TO}
            secondaryTitle={props.shipTo?.companyName}
            isExpanded={props.step === STEP_SHIP_TO}
            onToggle={(expanded) => {
                if (expanded) {
                    props.setStep(STEP_SHIP_TO);
                } else {
                    props.setStep(STEP_NONE);
                }
            }}
            disabled={props.disabled}
        >
            <MessageBar messageBarType={MessageBarType.info} styles={{ root: { marginBottom: '0.5em' } }}>{hint}</MessageBar>
            <CompanyForm
                company={props.shipTo ?? {}}
                onChange={props.onChange}
                restrictToProcurementContactType={props.dropship ? undefined : 'shipping'}
            />
        </AccordionBox>
    );
}

/**
 * Bill-to for a new order. Always restricted to designated procurement billing contacts — no manual
 * address entry — regardless of dropship.
 */
export const BillToStep: React.FC<{
    billTo?: IProcurementCompany;
    onChange: (billTo?: IProcurementCompany) => void;
    step: string;
    setStep: (step: string) => void;
    disabled?: boolean;
}> = (props) => {
    return (
        <AccordionBox
            title={STEP_BILL_TO}
            secondaryTitle={props.billTo?.companyName}
            isExpanded={props.step === STEP_BILL_TO}
            onToggle={(expanded) => {
                if (expanded) {
                    props.setStep(STEP_BILL_TO);
                } else {
                    props.setStep(STEP_NONE);
                }
            }}
            disabled={props.disabled}
        >
            <MessageBar messageBarType={MessageBarType.info} styles={{ root: { marginBottom: '0.5em' } }}>
                Only contacts marked as a procurement billing contact can be selected.
            </MessageBar>
            <CompanyForm
                company={props.billTo ?? {}}
                onChange={props.onChange}
                restrictToProcurementContactType='billing'
            />
        </AccordionBox>
    );
}

export const FindExistingOrderStep: React.FC<{
    selectedOrderId?: string;
    onOrderSelected: (orderId?: string) => void;
    vendor?: string;
    step: string;
    setStep: (step: string) => void;
    disabled?: boolean;
}> = (props) => {
    return (
        <AccordionBox
            title={`Existing Order`}
            isExpanded={props.step === STEP_EXISTING_ORDERS}
            onToggle={(expanded) => {
                if (expanded) {
                    props.setStep(STEP_EXISTING_ORDERS);
                } else {
                    props.setStep(STEP_NONE);
                }
            }}
            disabled={props.disabled}
        >
            <ExistingOrderSearch
                selectedOrderId={props.selectedOrderId}
                vendor={props.vendor}
                onOrderSelected={order => {
                    props.onOrderSelected(order.id);
                }}
            />
        </AccordionBox>
    );
}

export default ProcureDemandPanel;